
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useInternalAuth } from './useInternalAuth';
import { pointsService } from '@/services/pointsService';
import { validateProfileCompletion } from '@/services/profileValidationService';
import { toast } from 'sonner';

export const usePoints = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [pointsData, setPointsData] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [transactionsLoaded, setTransactionsLoaded] = useState(false);
  const [daysUntilReset, setDaysUntilReset] = useState<number>(0);
  const [membershipDuration, setMembershipDuration] = useState<string>('');

  const user = supabaseUser || internalUser;
  const isInternalUser = !!internalUser && !supabaseUser;
  
  // Use stable user ID reference to prevent re-renders from object changes
  const userId = user?.id;
  const loadedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (userId) {
      // Only reload data if user ID changed or first load
      if (loadedUserIdRef.current !== userId) {
        loadedUserIdRef.current = userId;
        loadPointsData();
        loadTransactions();
        loadTasks();
      }
    } else {
      loadedUserIdRef.current = null;
      setLoading(false);
    }
  }, [userId, isInternalUser]);

  const calculateDaysUntilReset = (yearStart: string): number => {
    const start = new Date(yearStart);
    const resetDate = new Date(start);
    resetDate.setFullYear(resetDate.getFullYear() + 1);
    const now = new Date();
    const diffMs = resetDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  };

  const getMembershipDuration = (createdAt: string): string => {
    const start = new Date(createdAt);
    const now = new Date();
    const years = now.getFullYear() - start.getFullYear();
    const months = now.getMonth() - start.getMonth() + (now.getDate() < start.getDate() ? -1 : 0);
    
    const adjustedMonths = months < 0 ? 12 + months : months;
    const adjustedYears = months < 0 ? years - 1 : years;
    
    if (adjustedYears > 0) {
      return `${adjustedYears} year${adjustedYears > 1 ? 's' : ''} ${adjustedMonths} month${adjustedMonths !== 1 ? 's' : ''}`;
    }
    return `${adjustedMonths} month${adjustedMonths !== 1 ? 's' : ''}`;
  };

  const loadPointsData = async () => {
    if (!user) return;

    try {
      console.log('Loading points data for user:', user.id, 'type:', isInternalUser ? 'internal' : 'supabase');
      
      const userType = isInternalUser ? 'internal' : 'supabase';
      
      // Check if user points record exists
      let { data: existingPoints, error: fetchError } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', user.id)
        .eq('user_type', userType)
        .single();

      if (fetchError && fetchError.code === 'PGRST116') {
        // No points record exists, create one
        console.log('No points record found, creating new one');
        const { data: newPoints, error: insertError } = await supabase
          .from('user_points')
          .insert({
            user_id: user.id,
            user_type: userType,
            total_points: 0,
            current_level: 'Bronze',
            referral_code: generateReferralCode()
          })
          .select()
          .single();

        if (insertError) {
          // Check if it's a duplicate key error (race condition)
          if (insertError.code === '23505') {
            console.log('Points record already exists (race condition), fetching existing record');
            // Another component already created the record, fetch it
            const { data: existingRecord, error: refetchError } = await supabase
              .from('user_points')
              .select('*')
              .eq('user_id', user.id)
              .eq('user_type', userType)
              .single();

            if (refetchError) {
              console.error('Error refetching points record:', refetchError);
              toast.error('Failed to load points data');
              return;
            }

            existingPoints = existingRecord;
          } else {
            console.error('Error creating points record:', insertError);
            toast.error('Failed to initialize points data');
            return;
          }
        } else {
          existingPoints = newPoints;
        }
      } else if (fetchError) {
        console.error('Error fetching points:', fetchError);
        toast.error('Failed to load points data');
        return;
      }

      setPointsData(existingPoints);
      
      // Calculate days until reset and membership duration
      if (existingPoints?.points_year_start) {
        setDaysUntilReset(calculateDaysUntilReset(existingPoints.points_year_start));
      }
      
      if (existingPoints?.created_at) {
        setMembershipDuration(getMembershipDuration(existingPoints.created_at));
      }
    } catch (error) {
      console.error('Error in loadPointsData:', error);
      toast.error('Failed to load points');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    if (!user) return;

    try {
      const userType = isInternalUser ? 'internal' : 'supabase';
      
      // Fetch ALL transactions for current year (no limit)
      const { data, error } = await supabase
        .from('points_transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('user_type', userType)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching transactions:', error);
        setTransactionsLoaded(true);
        return;
      }

      setTransactions(data || []);
      setTransactionsLoaded(true);
    } catch (error) {
      console.error('Error in loadTransactions:', error);
      setTransactionsLoaded(true);
    }
  };

  const loadTasks = async () => {
    if (!user) return;

    try {
      const userType = isInternalUser ? 'internal' : 'supabase';
      
      const { data, error } = await supabase
        .from('user_tasks')
        .select('*')
        .eq('user_id', user.id)
        .eq('user_type', userType)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tasks:', error);
        return;
      }

      setTasks(data || []);
    } catch (error) {
      console.error('Error in loadTasks:', error);
    }
  };

  const generateReferralCode = () => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const awardPoints = async (points: number, transactionType: string, description?: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const userType = isInternalUser ? 'internal' : 'supabase';
    
    console.log(`Attempting to award ${points} points of type ${transactionType} to user ${user.id}`);
    
      const { data: transactionId, error } = await supabase.rpc('award_points', {
        p_user_id: user.id,
        p_user_type: userType,
        p_points: points,
        p_transaction_type: transactionType,
        p_description: description,
        p_metadata: {}
      });

    if (error) {
      console.error('Error awarding points:', error);
      
      // Check for duplicate daily bonus
      if (error.message && error.message.includes('Daily bonus already claimed')) {
        const duplicateError = new Error(error.message);
        (duplicateError as any).code = 'DUPLICATE_TRANSACTION';
        throw duplicateError;
      }
      
      // Throw the error so caller can handle it
      throw error;
    }

    console.log('Points awarded successfully, transaction ID:', transactionId);
    
    // Wait to ensure database transaction is fully committed
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Reload data to reflect changes
    await loadPointsData();
    await loadTransactions();
  };

  const processReferral = async (referralCode: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const userType = isInternalUser ? 'internal' : 'supabase';
      
      const success = await pointsService.processReferral(referralCode, user.id, userType);
      
      if (success) {
        toast.success('Referral code applied successfully! You earned 50 points.');
        await loadPointsData();
        await loadTransactions();
        return true;
      } else {
        toast.error('Invalid referral code or already used.');
        return false;
      }
    } catch (error) {
      console.error('Error processing referral:', error);
      toast.error('Failed to process referral code');
      return false;
    }
  };

  const completeTask = async (taskId: string, pointsReward: number): Promise<void> => {
    if (!user) return;

    try {
      const success = await pointsService.completeTask(taskId);
      
      if (success) {
        // Award points for completing the task
        await awardPoints(pointsReward, 'task_complete', 'Task completed');
        
        // Reload tasks to reflect completion
        await loadTasks();
        
        toast.success(`Task completed! You earned ${pointsReward} points.`);
      } else {
        toast.error('Failed to complete task');
      }
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  const checkAndAwardProfileCompletion = async (profile: any, userRole: string) => {
    if (!user || !profile || !userRole) return;

    const validation = validateProfileCompletion(profile, userRole);
    const userType = isInternalUser ? 'internal' : 'supabase';
    
    if (validation.isComplete) {
      // Check if user already has profile completion bonus (handle null transactions)
      const hasProfileCompletionBonus = (transactions || []).some(
        (transaction) => transaction.transaction_type === 'profile_complete'
      );

      if (!hasProfileCompletionBonus) {
        try {
          await awardPoints(50, 'profile_complete', 'Profile completion bonus - all required fields completed');
          return true;
        } catch (error) {
          console.error('Error awarding profile completion bonus:', error);
        }
      }
    }
    
    return false;
  };

  return {
    pointsData,
    transactions,
    tasks,
    loading,
    transactionsLoaded,
    daysUntilReset,
    membershipDuration,
    awardPoints,
    processReferral,
    completeTask,
    checkAndAwardProfileCompletion,
    refetch: () => {
      setTransactionsLoaded(false);
      loadPointsData();
      loadTransactions();
      loadTasks();
    }
  };
};
